import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import {
  FaBan,
  FaCheckCircle,
  FaEdit,
  FaFilter,
  FaImage,
  FaList,
  FaPlay,
  FaSearch,
  FaThLarge,
  FaTrashAlt,
  FaUpload,
  FaVideo,
  FaWindowClose,
} from "react-icons/fa";
import ConfirmModal from "../../components/ConfirmModal";
import {
  MEDIA_UPLOAD_STATUS,
  TV_MEDIA_FILTERS,
  TV_MEDIA_SORT,
  TV_MEDIA_TYPES,
  TV_MEDIA_VIEW,
  TV_SUPPORTED_MEDIA_ACCEPT,
} from "../../services/tv-disply/tvDisplayConstants";
import {
  buildDefaultMediaName,
  deleteTvMedia,
  detectTvMediaType,
  formatFileSize,
  renameTvMedia,
  setTvMediaEnabled,
  uploadTvMedia,
  validateTvMediaFile,
} from "../../services/tv-disply/tvMediaService";
import "./TvMediaLibrary.css";

const FILTER_OPTIONS = [
  { value: TV_MEDIA_FILTERS.ALL, label: "All" },
  { value: TV_MEDIA_FILTERS.VIDEO, label: "Videos" },
  { value: TV_MEDIA_FILTERS.IMAGE, label: "Images" },
  { value: TV_MEDIA_FILTERS.ACTIVE, label: "Active" },
  { value: TV_MEDIA_FILTERS.DISABLED, label: "Disabled" },
];

const SORT_OPTIONS = [
  { value: TV_MEDIA_SORT.NEWEST, label: "Newest" },
  { value: TV_MEDIA_SORT.OLDEST, label: "Oldest" },
  { value: TV_MEDIA_SORT.NAME_ASC, label: "Name A-Z" },
  { value: TV_MEDIA_SORT.NAME_DESC, label: "Name Z-A" },
  { value: TV_MEDIA_SORT.SIZE_ASC, label: "Largest" },
  { value: TV_MEDIA_SORT.SIZE_DESC, label: "Smallest" },
];

export default function TvMediaLibrary({
  mediaRows = [],
  mediaLoading = false,
  mediaError = "",
  mediaStats,
}) {
  const fileInputRef = useRef(null);
  const uploadControllersRef = useRef(new Map());
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [filter, setFilter] = useState(TV_MEDIA_FILTERS.ALL);
  const [sort, setSort] = useState(TV_MEDIA_SORT.NEWEST);
  const [view, setView] = useState(TV_MEDIA_VIEW.GRID);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [queue, setQueue] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);
  const [renameState, setRenameState] = useState({ open: false, item: null, value: "", error: "", busy: false });
  const [deleteState, setDeleteState] = useState({ open: false, item: null, busy: false, error: "" });
  const [busyMediaId, setBusyMediaId] = useState("");
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const controllers = uploadControllersRef.current;
    return () => {
      controllers.forEach((controller) => controller?.cancel?.());
      controllers.clear();
    };
  }, []);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = window.setTimeout(() => setFeedback(null), 3200);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  useEffect(() => {
    if (!previewItem && !renameState.open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (renameState.open) {
        setRenameState({ open: false, item: null, value: "", error: "", busy: false });
        return;
      }
      setPreviewItem(null);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [previewItem, renameState.open]);

  const visibleRows = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    let rows = [...mediaRows];

    if (query) {
      rows = rows.filter((row) => row.searchText.includes(query));
    }

    if (filter === TV_MEDIA_FILTERS.VIDEO) rows = rows.filter((row) => row.type === TV_MEDIA_TYPES.VIDEO);
    if (filter === TV_MEDIA_FILTERS.IMAGE) rows = rows.filter((row) => row.type === TV_MEDIA_TYPES.IMAGE);
    if (filter === TV_MEDIA_FILTERS.ACTIVE) rows = rows.filter((row) => row.enabled);
    if (filter === TV_MEDIA_FILTERS.DISABLED) rows = rows.filter((row) => !row.enabled);

    rows.sort((left, right) => sortMediaRows(left, right, sort));
    return rows;
  }, [deferredSearch, filter, mediaRows, sort]);

  const hasFilters = Boolean(search.trim()) || filter !== TV_MEDIA_FILTERS.ALL;
  const queueActive = queue.some((item) => item.status === MEDIA_UPLOAD_STATUS.UPLOADING || item.status === MEDIA_UPLOAD_STATUS.WAITING);

  function onBrowseFiles() {
    fileInputRef.current?.click();
  }

  function handleFileSelection(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    enqueueFiles(files);
    event.target.value = "";
  }

  function enqueueFiles(files) {
    const nextItems = files.map((file) => createQueueItem(file));
    setQueue((current) => [...nextItems, ...current].slice(0, 60));
    nextItems.forEach((item) => startUpload(item));
  }

  function startUpload(item) {
    uploadControllersRef.current.delete(item.id);
    setQueueItem(item.id, {
      status: MEDIA_UPLOAD_STATUS.WAITING,
      progress: 0,
      error: "",
    });

    const validation = validateTvMediaFile(item.file);
    if (!validation.ok) {
      setQueueItem(item.id, {
        status: MEDIA_UPLOAD_STATUS.FAILED,
        error: validation.message,
      });
      return;
    }

    try {
      const controller = uploadTvMedia({
        file: item.file,
        onUpdate: (patch) => {
          setQueueItem(item.id, {
            ...patch,
            progress: patch.progress ?? 0,
          });
        },
      });

      uploadControllersRef.current.set(item.id, controller);
      controller.promise
        .then(() => {
          uploadControllersRef.current.delete(item.id);
          setFeedback({
            type: "success",
            message: `${item.file.name} uploaded successfully.`,
          });
        })
        .catch((error) => {
          uploadControllersRef.current.delete(item.id);
          setFeedback({
            type: "error",
            message: error?.message || "Media upload failed.",
          });
        });
    } catch (error) {
      setQueueItem(item.id, {
        status: MEDIA_UPLOAD_STATUS.FAILED,
        error: error?.message || "Media upload failed.",
      });
    }
  }

  function cancelUpload(queueId) {
    const controller = uploadControllersRef.current.get(queueId);
    controller?.cancel?.();
  }

  function retryUpload(queueId) {
    const target = queue.find((entry) => entry.id === queueId);
    if (!target) return;
    startUpload(target);
  }

  function setQueueItem(queueId, patch) {
    setQueue((current) =>
      current.map((entry) =>
        entry.id === queueId
          ? { ...entry, ...patch, updatedAt: Date.now() }
          : entry
      )
    );
  }

  function handleDrop(event) {
    event.preventDefault();
    setDragOver(false);
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    enqueueFiles(files);
  }

  function openRename(item) {
    setRenameState({
      open: true,
      item,
      value: item.name || buildDefaultMediaName(item.originalName || ""),
      error: "",
      busy: false,
    });
  }

  async function submitRename() {
    const nextName = String(renameState.value || "").trim();
    if (!nextName) {
      setRenameState((current) => ({ ...current, error: "Media name cannot be empty." }));
      return;
    }
    if (nextName.length > 120) {
      setRenameState((current) => ({ ...current, error: "Media name is too long." }));
      return;
    }

    try {
      setRenameState((current) => ({ ...current, busy: true, error: "" }));
      await renameTvMedia(renameState.item.id, nextName);
      setRenameState({ open: false, item: null, value: "", error: "", busy: false });
      setFeedback({ type: "success", message: "Media name updated." });
    } catch (error) {
      setRenameState((current) => ({
        ...current,
        busy: false,
        error: error?.message || "Unable to rename media.",
      }));
    }
  }

  async function toggleEnabled(item) {
    try {
      setBusyMediaId(item.id);
      await setTvMediaEnabled(item.id, !item.enabled);
      setFeedback({
        type: "success",
        message: item.enabled ? "Media disabled." : "Media enabled.",
      });
    } catch (error) {
      setFeedback({
        type: "error",
        message: error?.message || "Unable to update media status.",
      });
    } finally {
      setBusyMediaId("");
    }
  }

  async function confirmDelete() {
    if (!deleteState.item) return;
    try {
      setDeleteState((current) => ({ ...current, busy: true }));
      await deleteTvMedia(deleteState.item);
      setDeleteState({ open: false, item: null, busy: false, error: "" });
      setFeedback({ type: "success", message: "Media deleted permanently." });
    } catch (error) {
      setDeleteState((current) => ({ ...current, busy: false, error: error?.message || "Unable to delete media." }));
      setFeedback({
        type: "error",
        message: error?.message || "Unable to delete media.",
      });
    }
  }

  return (
    <section className="tv-display-panel">
      <div className="tv-display-panel-head">
        <div>
          <h2 className="tv-display-panel-title">Media Library</h2>
          <p className="tv-display-panel-copy">
            Upload MP4 videos and supported image files into the shared TV Display library. This library is
            realtime, multi-upload aware, and ready for future playlist selection.
          </p>
        </div>
      </div>

      <div className="tv-media-library">
        <div className="tv-media-summary-grid">
          <SummaryCard label="Total Media" value={mediaStats.total} />
          <SummaryCard label="Videos" value={mediaStats.videos} />
          <SummaryCard label="Images" value={mediaStats.images} />
          <SummaryCard label="Active" value={mediaStats.active} />
          <SummaryCard label="Disabled" value={mediaStats.disabled} />
        </div>

        <div className="tv-media-toolbar">
          <div className="tv-media-toolbar-left">
            <button type="button" className="tv-media-button" onClick={() => setUploadOpen(true)}>
              <FaUpload />
              <span>Upload Media</span>
            </button>

            <div className="tv-media-search">
              <FaSearch color="#64748b" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search media..."
              />
            </div>
          </div>

          <div className="tv-media-toolbar-right">
            <select className="tv-media-select" value={sort} onChange={(event) => setSort(event.target.value)}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            <div className="tv-media-view-toggle">
              <button
                type="button"
                className="tv-media-icon-button"
                onClick={() => setView(TV_MEDIA_VIEW.GRID)}
                aria-label="Grid view"
              >
                <FaThLarge color={view === TV_MEDIA_VIEW.GRID ? "#f20c53" : "#0f172a"} />
              </button>
              <button
                type="button"
                className="tv-media-icon-button"
                onClick={() => setView(TV_MEDIA_VIEW.LIST)}
                aria-label="List view"
              >
                <FaList color={view === TV_MEDIA_VIEW.LIST ? "#f20c53" : "#0f172a"} />
              </button>
            </div>
          </div>
        </div>

        <div className="tv-media-chip-row">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`tv-media-chip ${filter === option.value ? "active" : ""}`}
              onClick={() => setFilter(option.value)}
            >
              <FaFilter size={11} />
              <span>{option.label}</span>
            </button>
          ))}
        </div>

        {mediaError ? <div className="tv-media-inline-error">{mediaError}</div> : null}

        {mediaLoading ? (
          <div className="tv-media-inline-success">Loading TV Display media library...</div>
        ) : null}

        {!mediaLoading && !visibleRows.length ? (
          hasFilters ? (
            <div className="tv-media-empty">
              <div className="tv-media-empty-title">No media matches your search.</div>
              <div className="tv-media-empty-copy">
                Try changing the search text, filter, or sorting options.
              </div>
            </div>
          ) : (
            <div className="tv-media-empty">
              <div className="tv-media-empty-title">No media uploaded yet.</div>
              <div className="tv-media-empty-copy">
                Upload images or MP4 videos to start building your TV advertising library.
              </div>
              <div style={{ marginTop: 16 }}>
                <button type="button" className="tv-media-button" onClick={() => setUploadOpen(true)}>
                  <FaUpload />
                  <span>Upload Media</span>
                </button>
              </div>
            </div>
          )
        ) : null}

        {visibleRows.length ? (
          <div className={`tv-media-grid ${view === TV_MEDIA_VIEW.LIST ? "is-list" : ""}`}>
            {visibleRows.map((item) => (
              <article key={item.id} className="tv-media-card">
                <button type="button" className="tv-media-thumb" onClick={() => setPreviewItem(item)}>
                  {item.type === TV_MEDIA_TYPES.IMAGE && item.downloadURL ? (
                    <img src={item.downloadURL} alt={item.name} />
                  ) : (
                    <div className="tv-media-video-placeholder">
                      <div className="tv-media-video-icon"><FaPlay /></div>
                      <div>Video preview</div>
                    </div>
                  )}
                </button>

                <div className="tv-media-body">
                  <div className="tv-media-top">
                    <div>
                      <div className="tv-media-name">{item.name || "Untitled media"}</div>
                      <div className="tv-media-original">{item.originalName}</div>
                    </div>
                  </div>

                  <div className="tv-media-badges">
                    <span className={`tv-media-badge ${item.type === TV_MEDIA_TYPES.VIDEO ? "is-video" : "is-image"}`}>
                      {item.type === TV_MEDIA_TYPES.VIDEO ? <FaVideo /> : <FaImage />}
                      {item.type}
                    </span>
                    <span className={`tv-media-badge ${item.enabled ? "is-active" : "is-disabled"}`}>
                      {item.enabled ? "Active" : "Disabled"}
                    </span>
                  </div>

                  <div className="tv-media-meta-grid">
                    <MetaCell label="Size" value={formatFileSize(item.sizeBytes)} />
                    <MetaCell label="Dimensions" value={formatDimensions(item)} />
                    <MetaCell label="Duration" value={formatDuration(item.durationSeconds)} />
                    <MetaCell label="Uploaded" value={formatDate(item.createdAt)} />
                  </div>

                  <div className="tv-media-actions">
                    <button type="button" className="tv-media-button-secondary" onClick={() => setPreviewItem(item)}>
                      Preview
                    </button>
                    <button type="button" className="tv-media-button-secondary" onClick={() => openRename(item)}>
                      <FaEdit />
                      <span>Edit / Rename</span>
                    </button>
                    <button
                      type="button"
                      className="tv-media-button-secondary"
                      onClick={() => toggleEnabled(item)}
                      disabled={busyMediaId === item.id}
                    >
                      {item.enabled ? <FaBan /> : <FaCheckCircle />}
                      <span>{item.enabled ? "Disable" : "Enable"}</span>
                    </button>
                    <button
                      type="button"
                      className="tv-media-button-secondary"
                      onClick={() => setDeleteState({ open: true, item, busy: false, error: "" })}
                    >
                      <FaTrashAlt />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </div>

      {uploadOpen ? (
        <UploadModal
          dragOver={dragOver}
          onClose={() => setUploadOpen(false)}
          onBrowse={onBrowseFiles}
          onDrop={handleDrop}
          onDragOver={() => setDragOver(true)}
          onDragLeave={() => setDragOver(false)}
          fileInputRef={fileInputRef}
          onFileSelection={handleFileSelection}
          queue={queue}
          queueActive={queueActive}
          onCancelUpload={cancelUpload}
          onRetryUpload={retryUpload}
        />
      ) : null}

      {previewItem ? (
        <PreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
      ) : null}

      {renameState.open ? (
        <RenameModal
          state={renameState}
          onChange={(value) => setRenameState((current) => ({ ...current, value, error: "" }))}
          onClose={() => setRenameState({ open: false, item: null, value: "", error: "", busy: false })}
          onSubmit={submitRename}
        />
      ) : null}

      <ConfirmModal
        open={deleteState.open}
        title="Delete this media?"
        message="This will remove the media from the TV Display media library and Firebase Storage."
        confirmText="Delete Permanently"
        cancelText="Cancel"
        loading={deleteState.busy}
        error={deleteState.error}
        onCancel={() => setDeleteState({ open: false, item: null, busy: false, error: "" })}
        onConfirm={confirmDelete}
      />

      {feedback ? (
        <div className={`tv-media-floating-toast ${feedback.type === "success" ? "is-success" : "is-error"}`}>
          {feedback.message}
        </div>
      ) : null}
    </section>
  );
}

function UploadModal({
  dragOver,
  onClose,
  onBrowse,
  onDrop,
  onDragOver,
  onDragLeave,
  fileInputRef,
  onFileSelection,
  queue,
  queueActive,
  onCancelUpload,
  onRetryUpload,
}) {
  return (
    <div className="tv-media-modal-overlay" onMouseDown={onClose}>
      <div className="tv-media-modal" onMouseDown={(event) => event.stopPropagation()}>
        <div className="tv-media-modal-head">
          <div>
            <h3 className="tv-media-modal-title">Upload Media</h3>
            <p className="tv-media-modal-copy">
              Upload MP4, JPG, JPEG, PNG, or WEBP files. Multiple files are supported, and each upload runs
              independently with its own progress and cancellation state.
            </p>
          </div>
          <button type="button" className="tv-media-icon-button" onClick={onClose} aria-label="Close upload modal">
            <FaWindowClose />
          </button>
        </div>

        <div
          className={`tv-media-dropzone ${dragOver ? "is-over" : ""}`}
          onDragEnter={(event) => {
            event.preventDefault();
            onDragOver();
          }}
          onDragOver={(event) => {
            event.preventDefault();
            onDragOver();
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            onDragLeave();
          }}
          onDrop={onDrop}
        >
          <div className="tv-media-dropzone-icon"><FaUpload /></div>
          <div className="tv-media-dropzone-title">Drag files here</div>
          <div className="tv-media-dropzone-copy">
            or choose MP4 videos and supported image files from your device.
          </div>
          <div style={{ marginTop: 14 }}>
            <button type="button" className="tv-media-button" onClick={onBrowse}>
              Browse Files
            </button>
          </div>
          <div className="tv-media-dropzone-foot">
            Supported: MP4, JPG, JPEG, PNG, WEBP
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={TV_SUPPORTED_MEDIA_ACCEPT}
            style={{ display: "none" }}
            onChange={onFileSelection}
          />
        </div>

        {queue.length ? (
          <div className="tv-media-upload-queue">
            {queue.map((item) => (
              <div
                key={item.id}
                className={`tv-media-upload-item is-${String(item.status || "").toLowerCase()}`}
              >
                <div className="tv-media-upload-head">
                  <div>
                    <div className="tv-media-upload-name">{item.file.name}</div>
                    <div className="tv-media-upload-sub">
                      {item.type || "Unsupported"} | {formatFileSize(item.file.size || 0)}
                    </div>
                  </div>
                  <div className="tv-media-upload-status">{item.status}</div>
                </div>

                <div className="tv-media-progress">
                  <div className="tv-media-progress-bar" style={{ width: `${item.progress || 0}%` }} />
                </div>

                <div className="tv-media-upload-row">
                  <div className={`tv-media-upload-message ${item.error ? "is-error" : ""}`}>
                    {item.error || queueLabel(item)}
                  </div>
                  {item.status === MEDIA_UPLOAD_STATUS.UPLOADING || item.status === MEDIA_UPLOAD_STATUS.WAITING ? (
                    <button type="button" className="tv-media-button-secondary" onClick={() => onCancelUpload(item.id)}>
                      Cancel
                    </button>
                  ) : null}
                  {(item.status === MEDIA_UPLOAD_STATUS.FAILED || item.status === MEDIA_UPLOAD_STATUS.CANCELLED) ? (
                    <button type="button" className="tv-media-button-secondary" onClick={() => onRetryUpload(item.id)}>
                      Retry
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        <div className="tv-media-modal-actions">
          <button type="button" className="tv-media-button-secondary" onClick={onClose}>
            {queueActive ? "Close and keep uploading" : "Done"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ item, onClose }) {
  return (
    <div className="tv-media-modal-overlay" onMouseDown={onClose}>
      <div className="tv-media-modal" onMouseDown={(event) => event.stopPropagation()}>
        <div className="tv-media-modal-head">
          <div>
            <h3 className="tv-media-modal-title">{item.name || "Media Preview"}</h3>
            <p className="tv-media-modal-copy">{item.originalName}</p>
          </div>
          <button type="button" className="tv-media-icon-button" onClick={onClose} aria-label="Close preview">
            <FaWindowClose />
          </button>
        </div>

        <div className="tv-media-preview-shell">
          <div className="tv-media-preview-stage">
            {item.type === TV_MEDIA_TYPES.IMAGE ? (
              <img src={item.downloadURL} alt={item.name} />
            ) : (
              <video
                className="tv-media-preview-video"
                src={item.downloadURL}
                controls
                preload="metadata"
                playsInline
                muted
              />
            )}
          </div>

          <div className="tv-media-preview-details">
            <MetaCell label="Filename" value={item.originalName} />
            <MetaCell label="Dimensions" value={formatDimensions(item)} />
            <MetaCell label="Size" value={formatFileSize(item.sizeBytes)} />
            <MetaCell label="Status" value={item.enabled ? "Active" : "Disabled"} />
          </div>
        </div>
      </div>
    </div>
  );
}

function RenameModal({ state, onChange, onClose, onSubmit }) {
  return (
    <div className="tv-media-modal-overlay" onMouseDown={onClose}>
      <div className="tv-media-modal is-small" onMouseDown={(event) => event.stopPropagation()}>
        <div className="tv-media-modal-head">
          <div>
            <h3 className="tv-media-modal-title">Rename Media</h3>
            <p className="tv-media-modal-copy">
              Update the display name only. The Firebase Storage file path will remain unchanged.
            </p>
          </div>
          <button type="button" className="tv-media-icon-button" onClick={onClose} aria-label="Close rename dialog">
            <FaWindowClose />
          </button>
        </div>

        <div className="tv-media-field">
          <label className="tv-media-modal-copy" htmlFor="tv-media-rename-input">Media name</label>
          <input
            id="tv-media-rename-input"
            className="tv-media-input"
            value={state.value}
            onChange={(event) => onChange(event.target.value)}
            maxLength={120}
            autoFocus
          />
          {state.error ? <div className="tv-media-inline-error">{state.error}</div> : null}
        </div>

        <div className="tv-media-modal-actions">
          <button type="button" className="tv-media-button-secondary" onClick={onClose} disabled={state.busy}>
            Cancel
          </button>
          <button type="button" className="tv-media-button" onClick={onSubmit} disabled={state.busy}>
            {state.busy ? "Saving..." : "Save Name"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="tv-media-summary-card">
      <div className="tv-media-summary-label">{label}</div>
      <div className="tv-media-summary-value">{value}</div>
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div className="tv-media-meta-item">
      <div className="tv-media-meta-label">{label}</div>
      <div className="tv-media-meta-value">{value || "-"}</div>
    </div>
  );
}

function createQueueItem(file) {
  const validation = validateTvMediaFile(file);
  return {
    id: `queue_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    file,
    type: validation.ok ? detectTvMediaType(file) : "",
    status: validation.ok ? MEDIA_UPLOAD_STATUS.WAITING : MEDIA_UPLOAD_STATUS.FAILED,
    progress: 0,
    error: validation.ok ? "" : validation.message,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function queueLabel(item) {
  if (item.status === MEDIA_UPLOAD_STATUS.UPLOADING) return `Uploading... ${item.progress || 0}%`;
  if (item.status === MEDIA_UPLOAD_STATUS.COMPLETED) return "Upload completed.";
  if (item.status === MEDIA_UPLOAD_STATUS.CANCELLED) return "Upload cancelled.";
  if (item.status === MEDIA_UPLOAD_STATUS.WAITING) return "Waiting to start upload.";
  return "Upload failed.";
}

function sortMediaRows(left, right, sort) {
  if (sort === TV_MEDIA_SORT.OLDEST) return left.createdAtMs - right.createdAtMs;
  if (sort === TV_MEDIA_SORT.NAME_ASC) return String(left.name || "").localeCompare(String(right.name || ""));
  if (sort === TV_MEDIA_SORT.NAME_DESC) return String(right.name || "").localeCompare(String(left.name || ""));
  if (sort === TV_MEDIA_SORT.SIZE_ASC) return (right.sizeBytes || 0) - (left.sizeBytes || 0);
  if (sort === TV_MEDIA_SORT.SIZE_DESC) return (left.sizeBytes || 0) - (right.sizeBytes || 0);
  return right.createdAtMs - left.createdAtMs;
}

function formatDimensions(item) {
  if (!item?.width || !item?.height) return "Unknown";
  return `${item.width} x ${item.height}`;
}

function formatDuration(value) {
  if (!Number.isFinite(Number(value)) || Number(value) <= 0) return "Not available";
  const total = Math.round(Number(value));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (!minutes) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

function formatDate(value) {
  if (!value) return "Unknown";
  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : typeof value?.seconds === "number"
        ? new Date(value.seconds * 1000)
        : new Date(value);
  if (!Number.isFinite(date.getTime())) return "Unknown";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}
